// First-login quick-setup screen (name, phone, profession).
import { useState, useEffect } from "react";
import { PhoneField } from "../ui/forms";
import { PrabzoLogo } from "../ui/primitives";

export const ProfileSetup=({user,onComplete})=>{
  const [name,setName]=useState(user?.displayName||"");
  const [phone,setPhone]=useState("");
  const [profession,setProfession]=useState("");
  const [saving,setSaving]=useState(false);
  const [errors,setErrors]=useState({});

  // displayName can arrive slightly after this component mounts (e.g. right after
  // signup, before Firebase's auth-state listener re-fires) — backfill it instead
  // of leaving the field empty and making the user retype the name they just gave.
  useEffect(()=>{
    if(!name&&user?.displayName)setName(user.displayName);
  },[user?.displayName]);

  const QUICK_PROFESSIONS=[
    {label:"Yoga Trainer",icon:"🧘"},
    {label:"Fitness Coach",icon:"💪"},
    {label:"Tutor",icon:"📚"},
    {label:"Coach",icon:"🎯"},
    {label:"Therapist",icon:"💆"},
    {label:"Dancer",icon:"💃"},
    {label:"Musician",icon:"🎵"},
    {label:"Photographer",icon:"📸"},
    {label:"Consultant",icon:"💼"},
    {label:"Nutritionist",icon:"🥗"},
    {label:"Other",icon:"✨"},
  ];

  const validate=()=>{
    const e={};
    if(!name.trim())e.name="Your name is required";
    if(!profession)e.profession="Please select your profession";
    return e;
  };

  const save=async()=>{
    const e=validate();
    if(Object.keys(e).length){setErrors(e);return;}
    setSaving(true);
    const labelMap={"Yoga Trainer":"Student","Fitness Coach":"Client","Tutor":"Student","Coach":"Client","Therapist":"Patient","Dancer":"Student","Musician":"Student","Photographer":"Client","Consultant":"Client","Nutritionist":"Client"};
    const sessionMap={"Yoga Trainer":"Session","Fitness Coach":"Session","Tutor":"Class","Coach":"Session","Therapist":"Session","Dancer":"Class","Musician":"Lesson","Photographer":"Shoot","Consultant":"Meeting","Nutritionist":"Consultation"};
    try{
      await onComplete({
        name:name.trim(),
        phone:phone.trim(),
        email:user?.email||"",
        profession,
        services:[profession==="Other"?"My Service":profession],
        clientLabel:labelMap[profession]||"Client",
        sessionLabel:sessionMap[profession]||"Session",
        currency:"₹",
        profileComplete:true,
        quickSetup:true, // flag to show complete profile nudge
      });
    }catch(e){setSaving(false);}
  };

  return(
    <div className="min-h-screen overflow-y-auto flex flex-col p-3" style={{background:"linear-gradient(135deg,#0f4c5c 0%,#0a7c8c 50%,#c8902a 100%)",paddingTop:`calc(env(safe-area-inset-top) + 0.75rem)`,paddingBottom:`calc(env(safe-area-inset-bottom) + 0.75rem)`}}>
      <div className="w-full max-w-sm mx-auto my-auto py-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3 shadow-lg" style={{background:"#0f766e",padding:"10px"}}>
            <PrabzoLogo/>
          </div>
          <h1 className="text-2xl font-black text-white" style={{fontFamily:"'Playfair Display',serif"}}>Quick Setup</h1>
          <p className="text-teal-200 text-sm mt-1">Just 2 things to get started 🚀</p>
        </div>
        <div className="bg-white rounded-3xl p-6 shadow-2xl space-y-5">

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Your Name <span className="text-red-400">*</span></label>
            <input className={`border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors ${errors.name?"border-red-400 bg-red-50":"border-gray-200 focus:border-teal-500"}`}
              placeholder="Rishabha Garg" value={name} onChange={e=>{setName(e.target.value);setErrors(ev=>({...ev,name:undefined}));}}/>
            {errors.name&&<p className="text-red-500 text-xs">{errors.name}</p>}
          </div>

          {/* Phone - optional */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>Mobile Number <span className="text-gray-300 font-normal">(optional)</span></label>
            <PhoneField value={phone} onChange={setPhone} error={errors.phone} hint="For WhatsApp reminders"/>
          </div>

          {/* Profession quick picker */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:"#6b7280"}}>I am a... <span className="text-red-400">*</span></label>
            <div className="grid grid-cols-3 gap-2">
              {QUICK_PROFESSIONS.map(p=>(
                <button key={p.label} type="button"
                  onClick={()=>{setProfession(p.label);setErrors(ev=>({...ev,profession:undefined}));}}
                  className="flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl border text-xs font-semibold transition-all"
                  style={{
                    borderColor:profession===p.label?"#0f766e":"#e5e7eb",
                    background:profession===p.label?"#f0fdf4":"#f9fafb",
                    color:profession===p.label?"#0f766e":"#374151",
                    transform:profession===p.label?"scale(1.05)":"scale(1)",
                  }}>
                  <span style={{fontSize:"20px"}}>{p.icon}</span>
                  <span style={{fontSize:"10px",textAlign:"center",lineHeight:1.2}}>{p.label}</span>
                </button>
              ))}
            </div>
            {errors.profession&&<p className="text-red-500 text-xs">{errors.profession}</p>}
          </div>

          {/* CTA */}
          <button onClick={save} disabled={saving}
            className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all"
            style={{background:saving?"#9ca3af":"#0f766e"}}>
            {saving?"Setting up...":"Get Started →"}
          </button>

          <p className="text-center text-xs" style={{color:"#9ca3af"}}>
            You can complete your full profile anytime in Settings
          </p>
        </div>
        <p className="text-center text-teal-300 text-xs mt-4">© 2026 Prabzo · Crafted by Shri Studio</p>
      </div>
    </div>
  );
};
